import { MessageSquare } from 'lucide-react';
import { useMe } from '../auth/MeContext';
import { Icon } from './Icon';
import { ClientSwitcher } from './ClientSwitcher';
import { NewConversationButton } from './NewConversationButton';
import { PreviousConversations } from './PreviousConversations';
import { UserMenu } from './UserMenu';
import { useT } from '../I18nContext';
import { useBrandingText } from '../branding/useBrandingText';

/**
 * Dashboard sidebar: brand at the top, primary action ("New
 * conversation"), nav (Ask / History), recent list placeholder,
 * user card at the bottom.
 *
 * D1 scope: layout only. The "New conversation" button, "Ask",
 * "History" buttons, and Recent list are visual placeholders.
 * They wire up in PR-D2 (chat) and PR-D5 (history modal).
 */
export function Sidebar() {
  const { me } = useMe();
  const t = useT();

  // Brand headline. Resolution order:
  //   1. branding.product_name in the active dashboard language
  //                              (via the per-language overrides
  //                              the tenant authored in Site
  //                              Vocabulary's Branding section)
  //   2. branding.product_name English  (from /admin/branding)
  //   3. client.name             (legacy display_name fallback)
  //   4. catalogue fallback      ('Locksmith' in English, translated
  //                              elsewhere)
  // useBrandingText handles steps 1+2 in one go.
  const branding = me?.current_client?.branding;
  const productNameForLang = useBrandingText('product_name');
  const orgName =
    productNameForLang.trim() ||
    me?.current_client?.name ||
    t('sidebar.brand_fallback');
  const customLogoUrl = branding?.logo_url ?? '';
  const userName = me ? `${me.user.first_name} ${me.user.last_name}`.trim() : '';
  const userEmail = me?.user.email ?? '';
  // 'U' isn't English text, it's a typographic placeholder for an
  // empty initials slot. Kept out of the catalogue.
  const initials = me?.user.initials ?? 'U';

  return (
    <aside className="dashboard-sidebar">
      {/* ── Brand ── */}
      <div className="sidebar-brand">
        <div className="sidebar-brand-mark">
          {/* If the client has uploaded its own logo we honour that;
              otherwise fall back to the theme-aware Locksmith icon
              (the glyph-only mark; the wordmark <Logo /> is for the
              auth shell where it has room to breathe). */}
          {customLogoUrl ? (
            <img
              src={customLogoUrl}
              alt={orgName}
              className="sidebar-brand-mark-img"
            />
          ) : (
            <Icon className="sidebar-brand-mark-img" alt={orgName} />
          )}
        </div>
        <div className="sidebar-brand-text">
          <div className="sidebar-brand-title">{orgName}</div>
          <ClientSwitcher />
        </div>
      </div>

      {/* ── Primary action ── */}
      <NewConversationButton />

      {/* ── Nav ── */}
      <nav className="sidebar-nav" aria-label={t('sidebar.nav_aria_label')}>
        <button
          type="button"
          className="sidebar-nav-item is-active"
          aria-current="page"
        >
          <MessageSquare size={14} />
          <span>{t('sidebar.nav_ask')}</span>
        </button>
        {/* History modal moved into the Previous Conversations
            list below; same data, always visible, one click less
            to find an old thread. */}
      </nav>

      <div className="sidebar-divider" />

      <div className="sidebar-section-label">{t('sidebar.previous_conversations_label')}</div>
      <div className="sidebar-recent">
        <PreviousConversations />
      </div>

      {/* ── User card ── */}
      <div className="sidebar-user">
        <div className="sidebar-user-avatar" aria-hidden>
          {initials}
        </div>
        <div className="sidebar-user-meta">
          <div className="sidebar-user-name">{userName || t('sidebar.user_name_fallback')}</div>
          <div className="sidebar-user-email">{userEmail}</div>
        </div>
        <UserMenu />
      </div>
    </aside>
  );
}
